import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormStep, FormStepConfig } from '../../types';

export interface StepIndicatorProps {
  steps: FormStepConfig[];
  currentStep: FormStep;
  completedSteps: FormStep[];
  onStepClick?: (step: FormStep) => void;
  orientation?: 'horizontal' | 'vertical';
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  orientation = 'horizontal',
}) => {
  const { t } = useTranslation();

  const getStepStatus = (stepId: FormStep): 'completed' | 'current' | 'upcoming' => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const isClickable = (stepId: FormStep): boolean => {
    return completedSteps.includes(stepId) || stepId === currentStep;
  };

  if (orientation === 'vertical') {
    return (
      <nav aria-label={t('accessibility.formProgress')}>
        <ol className="space-y-4">
          {steps.map((step, index) => {
            const status = getStepStatus(step.id);
            const clickable = isClickable(step.id);

            return (
              <li key={step.id} className="relative">
                {index !== steps.length - 1 && (
                  <div
                    className={`absolute left-4 top-10 w-0.5 h-full -ml-px ${
                      status === 'completed' ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  />
                )}

                <button
                  type="button"
                  onClick={() => clickable && onStepClick?.(step.id)}
                  disabled={!clickable}
                  aria-current={status === 'current' ? 'step' : undefined}
                  aria-label={t('accessibility.stepLabel', { number: index + 1, title: step.title, status: status === 'completed' ? t('accessibility.stepCompleted') : status === 'current' ? t('accessibility.stepCurrent') : t('accessibility.stepPending') })}
                  className={`
                    relative flex items-start group
                    ${clickable ? 'cursor-pointer' : 'cursor-not-allowed'}
                  `}
                >
                  <span className="flex items-center h-9">
                    <span
                      className={`
                        relative z-10 w-8 h-8 flex items-center justify-center rounded-full
                        transition-all duration-200
                        ${
                          status === 'completed'
                            ? 'bg-green-600 text-white'
                            : status === 'current'
                            ? 'bg-white border-2 border-green-600 text-green-600'
                            : 'bg-white border-2 border-gray-300 text-gray-500'
                        }
                        ${clickable && status !== 'completed' ? 'group-hover:border-green-400' : ''}
                      `}
                    >
                      {status === 'completed' ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <span className="text-sm font-medium">{index + 1}</span>
                      )}
                    </span>
                  </span>

                  <span className="ml-4 min-w-0">
                    <span
                      className={`
                        text-sm font-medium
                        ${status === 'current' ? 'text-green-600' : 'text-gray-900'}
                      `}
                    >
                      {step.title}
                    </span>
                    <span className="text-sm text-gray-500 block">{step.description}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  // Horizontal orientation
  return (
    <nav aria-label={t('accessibility.formProgress')} className="mb-8">
      <ol className="flex items-center justify-between">
        {steps.map((step, index) => {
          const status = getStepStatus(step.id);
          const clickable = isClickable(step.id);
          const isLast = index === steps.length - 1;

          return (
            <li key={step.id} className={`relative ${isLast ? '' : 'flex-1'}`}>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => clickable && onStepClick?.(step.id)}
                  disabled={!clickable}
                  aria-current={status === 'current' ? 'step' : undefined}
                  aria-label={t('accessibility.stepLabel', { number: index + 1, title: step.title, status: status === 'completed' ? t('accessibility.stepCompleted') : status === 'current' ? t('accessibility.stepCurrent') : t('accessibility.stepPending') })}
                  className={`
                    relative flex flex-col items-center group
                    ${clickable ? 'cursor-pointer' : 'cursor-not-allowed'}
                  `}
                >
                  <span
                    className={`
                      w-10 h-10 flex items-center justify-center rounded-full
                      transition-all duration-200
                      ${
                        status === 'completed'
                          ? 'bg-green-600 text-white'
                          : status === 'current'
                          ? 'bg-white border-2 border-green-600 text-green-600'
                          : 'bg-white border-2 border-gray-300 text-gray-500'
                      }
                      ${clickable && status !== 'completed' ? 'group-hover:border-green-400' : ''}
                    `}
                  >
                    {status === 'completed' ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </span>

                  <span
                    className={`
                      mt-2 text-xs font-medium text-center max-w-[80px]
                      ${status === 'current' ? 'text-green-600' : 'text-gray-500'}
                    `}
                  >
                    {step.title}
                  </span>
                </button>

                {!isLast && (
                  <div
                    className={`
                      flex-1 h-0.5 mx-4
                      ${status === 'completed' ? 'bg-green-600' : 'bg-gray-300'}
                    `}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default StepIndicator;
